import React from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Button } from "react-bootstrap";
import { FormattedMessage, useIntl } from "react-intl";
import styled from "styled-components";
import { upgradeManagerMessages } from "../upgradeManager.messages";
import { ActionButton } from "../styles";

const DateRangeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
`;

const DatePickerWrapper = styled.div`
  flex: 1;
  min-width: 0;

  input.react-datepicker-ignore-onclickoutside,
  input {
    display: block;
    width: 100%;
    padding: 0.375rem 0.75rem;
    font-size: 1rem;
    line-height: 1.5;
    color: #495057;
    background-color: #fff;
    background-clip: padding-box;
    border: 1px solid #ced4da;
    border-radius: 0.25rem;
    transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
  }
`;

export interface DateRange {
  start?: Date | string;
  end?: Date | string;
}

interface DateRangesInputProps {
  value: DateRange[];
  onChange: (ranges: DateRange[]) => void;
}

const toDate = (val: Date | string | undefined): Date | undefined => {
  if (!val) return undefined;
  if (val instanceof Date) return val;
  const d = new Date(val);
  return isNaN(d.getTime()) ? undefined : d;
};

export const DateRangesInput = ({ value = [], onChange }: DateRangesInputProps): JSX.Element => {
  const intl = useIntl();
  const messages = upgradeManagerMessages;

  const handleAddRange = () => {
    onChange([...value, { start: undefined, end: undefined }]);
  };

  const handleRemoveRange = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
  };

  const handleChangeStart = (index: number, date: Date | null) => {
    const updated = [...value];
    updated[index] = { ...updated[index], start: date ?? undefined };
    onChange(updated);
  };

  const handleChangeEnd = (index: number, date: Date | null) => {
    const updated = [...value];
    updated[index] = { ...updated[index], end: date ?? undefined };
    onChange(updated);
  };

  return (
    <div>
      {value.map((range, index) => {
        const startDate = toDate(range.start);
        const endDate = toDate(range.end);
        return (
          <DateRangeRow key={index}>
            <DatePickerWrapper>
              <DatePicker
                selected={startDate ?? null}
                onChange={(date: Date | null) => handleChangeStart(index, date)}
                selectsStart
                startDate={startDate}
                endDate={endDate}
                dateFormat="yyyy-MM-dd"
                placeholderText={intl.formatMessage(messages.startDate)}
                isClearable
                showMonthDropdown
                showYearDropdown
              />
            </DatePickerWrapper>
            <DatePickerWrapper>
              <DatePicker
                selected={endDate ?? null}
                onChange={(date: Date | null) => handleChangeEnd(index, date)}
                selectsEnd
                startDate={startDate}
                endDate={endDate}
                minDate={startDate}
                dateFormat="yyyy-MM-dd"
                placeholderText={intl.formatMessage(messages.endDate)}
                isClearable
                showMonthDropdown
                showYearDropdown
              />
            </DatePickerWrapper>
            <ActionButton size="sm" variant="danger" onClick={() => handleRemoveRange(index)}>
              <i className="fa fa-trash-o" />
            </ActionButton>
          </DateRangeRow>
        );
      })}
      <Button variant="outline-secondary" size="sm" onClick={handleAddRange}>
        <FormattedMessage {...messages.addDateRange} />
      </Button>
    </div>
  );
};
